import React, {useState} from 'react';
import Content from "../../components/content/Content";
import DataTable from "../../components/data-table/DataTable";
import RowActionsMenu from "../../components/data-table/RowActionsMenu";
import {useTranslation} from "react-i18next";
import {
    createErpBusinessUnitMapping,
    deleteErpBusinessUnitMapping,
    editErpBusinessUnitMapping,
    getBusinessUnitsSmsGatewayAppActivityData,
    getTransportsForServiceMessage,
} from "../../services/smsGateway";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Switch} from "@material-ui/core";
import ActionButton from "../../components/button/ActionButton";
import AnyField from "../../components/fields/AnyField";

const emptyMapping = {
    id: null,
    mappedMobileAppId: '',
    businessUnitId: '',
    forServiceMessages: true,
    serviceMessagesErpToCrnEnabled: false,
    b24ChatId: '',
    whatsAppMessengerTransport: null,
    maxMessengerTransport: null,
    telegramMessengerTransport: null,
};

const transport_field_vendor = {
    whatsAppMessengerTransport: 'whatsappService',
    maxMessengerTransport: 'maxService',
    telegramMessengerTransport: 'telegramService',
};

const getTableData = (page, per_page, order, order_by) => {
    return getBusinessUnitsSmsGatewayAppActivityData(page, per_page, order, order_by);
}

export default function MessagesPage() {
    const {t} = useTranslation();

    const [refreshTable, setRefreshTable] = useState(false);
    const [isOpenCreateDialog, setIsOpenCreateDialog] = useState(false);
    const [isOpenEditDialog, setIsOpenEditDialog] = useState(false);
    const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
    const [rowData, setRowData] = useState(emptyMapping);
    const [errorMessage, setErrorMessage] = useState('');
    const [availableTransports, setAvailableTransports] = useState({});
    const [initialTransports, setInitialTransports] = useState({});

    const columnData = [
        {id: 'mappedMobileAppId', numeric: false, disablePadding: false, label: 'Mapped mobile app Id'},
        {id: 'lastPollingAt', label: 'Date of last polling', dateFormat: "DD.MM.YYYY HH:mm:ss"},
        {id: 'lastSucceededSentMessageDate', label: 'Date of last succeeded sent message', dateFormat: "DD.MM.YYYY HH:mm:ss"},
        {id: 'forServiceMessages', numeric: false, disablePadding: false, label: 'App for erp service messages'},
        {id: 'serviceMessagesErpToCrnEnabled', numeric: false, disablePadding: false, label: 'Service messages erp to crn enabled'},
        {id: 'batteryLevel', numeric: false, disablePadding: false, label: 'Phone battery level'},
        {id: 'activeSimName', numeric: false, disablePadding: false, label: 'Active sim name'},
        {id: 'b24ChatId', numeric: false, disablePadding: false, label: 'Bitrix24 chat id'},
        {
            id: 'whatsAppTransport',
            numeric: false,
            disableSorting: true,
            label: 'Service transport WhatsApp',
            processValue: (val, translate, itemRow) => itemRow.serviceTransports?.whatsappService?.name || '-',
        },
        {
            id: 'maxTransport',
            numeric: false,
            disableSorting: true,
            label: 'Service transport Max',
            processValue: (val, translate, itemRow) => itemRow.serviceTransports?.maxService?.name || '-',
        },
        {
            id: 'telegramTransport',
            numeric: false,
            disableSorting: true,
            label: 'Service transport Telegram',
            processValue: (val, translate, itemRow) => itemRow.serviceTransports?.telegramService?.name || '-',
        },
        {
            id: 'actions',
            numeric: true,
            disableSorting: true,
            label: '',
            processValue: (val, translate, itemRow) => (
                <RowActionsMenu
                    onEdit={() => openEditDialog(itemRow)}
                    onDelete={() => openDeleteDialog(itemRow)}
                />
            ),
        },
    ];

    const rowStyle = (item) => {
        if (item?.isAppSimCardProblemExists) {
            return {backgroundColor: '#da8383'};
        }
    };

    const fetchAvailableTransports = () => {
        getTransportsForServiceMessage().then(response => {
            if (response?.serviceTransports) {
                setAvailableTransports(response.serviceTransports);
            }
        });
    };

    const openCreateDialog = () => {
        setRowData(emptyMapping);
        setInitialTransports({});
        fetchAvailableTransports();
        setIsOpenCreateDialog(true);
    };

    const closeCreateDialog = () => {
        setIsOpenCreateDialog(false);
    };

    const openEditDialog = (row) => {
        const transports = {
            whatsAppMessengerTransport: row.serviceTransports?.whatsappService || null,
            maxMessengerTransport: row.serviceTransports?.maxService || null,
            telegramMessengerTransport: row.serviceTransports?.telegramService || null,
        };

        setInitialTransports(transports);
        setRowData({
            id: row.id,
            mappedMobileAppId: row.mappedMobileAppId,
            businessUnitId: row.businessUnitId,
            forServiceMessages: row.forServiceMessages,
            b24ChatId: row.b24ChatId,
            ...transports,
        });
        fetchAvailableTransports();
        setIsOpenEditDialog(true);
    };

    const closeEditDialog = () => {
        setIsOpenEditDialog(false);
    };

    const openDeleteDialog = (row) => {
        setRowData(row);
        setIsOpenDeleteDialog(true);
    };

    const closeDeleteDialog = () => {
        setIsOpenDeleteDialog(false);
    };

    const handleChange = (prop) => event => {
        setRowData({
            ...rowData,
            [prop]: event.target.value,
        });
    };

    const handleChangeForServiceMessages = event => {
        const checked = event.target.checked;

        setRowData({
            ...rowData,
            forServiceMessages: checked,
            ...(checked ? {} : {
                whatsAppMessengerTransport: null,
                maxMessengerTransport: null,
                telegramMessengerTransport: null,
            }),
        });
    };

    const getTransportOptions = (prop) => {
        const vendor = transport_field_vendor[prop];
        const options = [...(availableTransports[vendor] || [])];

        [rowData[prop], initialTransports[prop]].forEach(transport => {
            if (transport && !options.some(option => option.id === transport.id)) {
                options.unshift(transport);
            }
        });

        return options;
    };

    const handleChangeTransport = (prop) => event => {
        const selectedId = event.target.value;
        const selected = getTransportOptions(prop).find(option => option.id === selectedId) || null;

        setRowData({
            ...rowData,
            [prop]: selected,
        });
    };

    const collectTransportIds = () => [
        rowData.whatsAppMessengerTransport?.id,
        rowData.maxMessengerTransport?.id,
        rowData.telegramMessengerTransport?.id,
    ].filter(Boolean);

    const buildCreatePayload = () => ({
        mappedMobileAppId: rowData.mappedMobileAppId,
        businessUnitId: rowData.businessUnitId,
        isForServiceMessages: !!rowData.forServiceMessages,
        b24ChatId: rowData.b24ChatId || null,
        transportIds: collectTransportIds(),
    });

    const buildEditPayload = () => ({
        b24ChatId: rowData.b24ChatId || null,
        transportsIds: collectTransportIds(),
    });

    const handleError = (response) => {
        setErrorMessage(response.error ? response.error.message : t('An error occurred'));
        setTimeout(() => setErrorMessage(''), 3000);
    };

    const isFormValid = () => !!rowData.mappedMobileAppId && !!rowData.businessUnitId;

    const createMapping = () => {
        if (!isFormValid()) {
            setErrorMessage(t('Fill in required fields'));
            setTimeout(() => setErrorMessage(''), 3000);
            return;
        }

        createErpBusinessUnitMapping(buildCreatePayload()).then(response => {
            if (response.success) {
                closeCreateDialog();
                setRefreshTable(!refreshTable);
            } else {
                handleError(response);
            }
        });
    };

    const editMapping = () => {
        if (!isFormValid()) {
            setErrorMessage(t('Fill in required fields'));
            setTimeout(() => setErrorMessage(''), 3000);
            return;
        }

        editErpBusinessUnitMapping(rowData.id, buildEditPayload()).then(response => {
            if (response.success) {
                closeEditDialog();
                setRefreshTable(!refreshTable);
            } else {
                handleError(response);
            }
        });
    };

    const deleteMapping = () => {
        deleteErpBusinessUnitMapping(rowData.id).then(response => {
            if (response.success) {
                closeDeleteDialog();
                setRefreshTable(!refreshTable);
            } else {
                handleError(response);
            }
        });
    };

    const renderTransportField = (prop, label) => (
        <AnyField
            id={prop}
            label={t(label)}
            value={rowData[prop]?.id || ''}
            onChange={handleChangeTransport(prop)}
            select
            fullWidth
            disabled={!rowData.forServiceMessages}
        >
            <MenuItem value="">{'-'}</MenuItem>
            {getTransportOptions(prop).map(option => (
                <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
            ))}
        </AnyField>
    );

    const renderTransportFields = () => (
        <>
            {renderTransportField('whatsAppMessengerTransport', t("Service transport WhatsApp"))}
            {renderTransportField('maxMessengerTransport', t("Service transport Max"))}
            {renderTransportField('telegramMessengerTransport', t("Service transport Telegram"))}
        </>
    );

    const renderFormFields = () => (
        <>
            <AnyField
                id="mappedMobileAppId"
                label={t("Mapped mobile app Id")}
                value={rowData.mappedMobileAppId}
                onChange={handleChange('mappedMobileAppId')}
                required
                fullWidth
            />
            <AnyField
                id="businessUnitId"
                label={t("Business unit")}
                value={rowData.businessUnitId}
                onChange={handleChange('businessUnitId')}
                required
                fullWidth
            />
            <AnyField
                id="b24ChatId"
                label={t("Bitrix24 chat id")}
                value={rowData.b24ChatId}
                onChange={handleChange('b24ChatId')}
                fullWidth
            />
            {renderTransportFields()}
            <FormControlLabel
                control={<Switch checked={!!rowData.forServiceMessages} onChange={handleChangeForServiceMessages}/>}
                label={t("App for erp service messages")}
            />
        </>
    );

    const renderFormFieldsForEdit = () => (
        <>
            <AnyField
                id="mappedMobileAppId"
                label={t("Mapped mobile app Id")}
                value={rowData.mappedMobileAppId}
                disabled
                fullWidth
            />
            <AnyField
                id="businessUnitId"
                label={t("Business unit")}
                value={rowData.businessUnitId}
                readOnly
                fullWidth
            />
            <AnyField
                id="b24ChatId"
                label={t("Bitrix24 chat id")}
                value={rowData.b24ChatId}
                onChange={handleChange('b24ChatId')}
                fullWidth
            />
            {renderTransportFields()}
            <FormControlLabel
                control={<Switch checked={!!rowData.forServiceMessages} disabled/>}
                label={t("App for erp service messages")}
            />
        </>
    );

    return (
        <Content title={t('Business units sms gateway app activity')}>
            <ActionButton fullWidth={false} onClick={openCreateDialog}>{t("Add record")}</ActionButton>

            <DataTable
                columnData={columnData}
                dataFunc={getTableData}
                selector={false}
                pagination={false}
                refresh={refreshTable}
                rowStyleFunc={rowStyle}
            />

            <Dialog open={isOpenCreateDialog} onClose={closeCreateDialog} fullWidth>
                <DialogTitle>{t("Add record")}</DialogTitle>
                <DialogContent>
                    {renderFormFields()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={createMapping} color="primary">{t("Save")}</Button>
                    <Button onClick={closeCreateDialog} color="primary">{t("Cancel")}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isOpenEditDialog} onClose={closeEditDialog} fullWidth>
                <DialogTitle>{t("Edit record")}</DialogTitle>
                <DialogContent>
                    {renderFormFieldsForEdit()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={editMapping} color="primary">{t("Save")}</Button>
                    <Button onClick={closeEditDialog} color="primary">{t("Cancel")}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isOpenDeleteDialog} onClose={closeDeleteDialog}>
                <DialogTitle>{t("Delete record")}</DialogTitle>
                <DialogActions>
                    <ActionButton onClick={deleteMapping} color="secondary">{t("Ok")}</ActionButton>
                    <ActionButton onClick={closeDeleteDialog} color="secondary">{t("Cancel")}</ActionButton>
                </DialogActions>
            </Dialog>

            <Dialog open={!!errorMessage}>
                <DialogTitle>{errorMessage}</DialogTitle>
            </Dialog>
        </Content>
    )
}
